import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Sparkles } from "lucide-react";
import { uploadImage } from "../../lib/images";
import { AI_GALLERY } from "../../lib/aiPrompt";

const uploaded = new Map();

const fileFor = (bg) => {
  if (!uploaded.has(bg.id)) {
    const p = fetch(bg.full).then((r) => r.blob()).then((b) => uploadImage(new File([b], `${bg.id}.jpg`, { type: "image/jpeg" })))
      .catch((e) => { uploaded.delete(bg.id); throw e; });
    uploaded.set(bg.id, p);
  }
  return uploaded.get(bg.id);
};

// One-tap ready-made AI backgrounds; each is uploaded once per visit, then reused.
export const BackgroundGallery = ({ current, onPicked }) => {
  const [busy, setBusy] = useState(null);
  const [picked, setPicked] = useState({});
  const pick = async (bg) => {
    setBusy(bg.id);
    try {
      const file = await fileFor(bg);
      setPicked((p) => ({ ...p, [bg.id]: file.id }));
      onPicked(file);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load that background – please try again."); } finally { setBusy(null); }
  };
  return (
    <div data-testid="bg-gallery">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600"><Sparkles className="h-3.5 w-3.5 text-fuchsia-500" /> Ready-made AI backgrounds</div>
      <div className="flex w-0 min-w-full snap-x gap-2 overflow-x-auto pb-2">
        {AI_GALLERY.map((bg) => {
          const active = current && picked[bg.id] === current;
          return (
            <button key={bg.id} type="button" onClick={() => pick(bg)} disabled={!!busy} aria-pressed={!!active} title={bg.name} data-testid={`bg-gallery-${bg.id}`}
              className={`relative w-[64px] shrink-0 snap-start border-2 transition-[border-color,transform] hover:-translate-y-0.5 disabled:cursor-wait ${active ? "border-slate-900 shadow-[3px_3px_0_0_#0f172a]" : "border-slate-200 hover:border-slate-900"}`}>
              <img src={bg.src} alt={`${bg.name} background`} loading="lazy" className="aspect-[2/3] w-full object-cover" />
              <span className="block truncate px-0.5 py-0.5 text-[9px] font-bold uppercase">{bg.name}</span>
              {busy === bg.id && <span className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 className="h-5 w-5 animate-spin" /></span>}
              {active && <span className="absolute right-0.5 top-0.5 bg-lime-400 p-0.5"><Check className="h-3 w-3" strokeWidth={3} /></span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};
